import { createContext, useContext, useEffect, useState, ReactNode } from 'react';

import {
  setSdkConfig,
  getSdkConfig,
  getRpcUrl,
  detectProgramSet,
  MAINNET_GENESIS,
  ATLASNET_GENESIS,
  DEVNET_GENESIS,
} from '@sly-rentals/core';
import * as audit from '@sly-rentals/core/audit';
import { setSdkConfig as setLegacySdkConfig } from '@sly-rentals/core/legacy';
import { setSdkConfig as setVaultSdkConfig, audit as vaultAudit } from '@sly-rentals/vault';

import { useNetworkConfig, type NetworkConfig } from '../hooks/useNetworkConfig';

/**
 * What the SDK will *actually* use, read back from its own config after every
 * update — not recomputed from the playground's state.
 *
 * The bar used to show the URL the user typed, which is not the same claim.
 * The two can diverge: the SDK keeps its own singleton, a partial
 * `setSdkConfig` can drop a key, and `programs` is resolved asynchronously
 * from the chain's genesis hash. When they disagree the request follows the
 * SDK, so the SDK is what gets displayed.
 */
export interface EffectiveSdkConfig {
  /** Undefined when the SDK cannot resolve an endpoint at all. */
  rpcUrl?: string;
  /** Program-address set the SDK resolved (mainnet / atlasnet / …). */
  programs?: string;
  /** Message from `getRpcUrl()` when it refuses to guess a cluster. */
  error?: string;
}

/** Ask the SDK what it resolved. Never throws — a refusal becomes `error`. */
function readEffectiveConfig(): EffectiveSdkConfig {
  const programs = getSdkConfig().programs;
  try {
    return { rpcUrl: getRpcUrl(), programs };
  } catch (err) {
    return { programs, error: err instanceof Error ? err.message : String(err) };
  }
}

export type NetworkContextValue = NetworkConfig & { effective: EffectiveSdkConfig };

const NetworkContext = createContext<NetworkContextValue | null>(null);

// Map a genesis hash back to the program-set alias the SDK expects in
// setSdkConfig({ programs }). Must match `ProgramSetAlias` in
// @sly-rentals/core (addresses.ts) — any other string silently falls
// through to atlasnet addresses, which on mainnet means SAGE/ATLAS
// pubkeys point at non-executable accounts and CPIs fail with
// "Unsupported program id".
function networkForGenesis(genesis: string): 'mainnet' | 'atlasnet' | undefined {
  if (genesis === MAINNET_GENESIS) return 'mainnet';
  if (genesis === ATLASNET_GENESIS) return 'atlasnet';
  if (genesis === DEVNET_GENESIS) return 'atlasnet';
  return undefined;
}

export function NetworkProvider({ children }: { children: ReactNode }) {
  const value = useNetworkConfig();
  const { rpcUrl, eventsEnabled } = value;
  const [effective, setEffective] = useState<EffectiveSdkConfig>(readEffectiveConfig);

  // Keep both SDK entry points (`@sly-rentals/core` kit + `/legacy` web3.js)
  // in sync with the user-selected RPC. Each entry maintains its own
  // config singleton, so a missed update on one side leaves it pointing
  // at the wrong cluster — the playground builds instructions through
  // `/legacy`, so that side must track the network bar too. Detect the
  // program set by querying the chain's genesis hash so the SDK uses the
  // right addresses (mainnet vs atlasnet vs devnet) regardless of which
  // RPC the user pasted.
  //
  // We also flip on the singleton SRSLY event listener (`audit.onSrslyEvent`)
  // and derive a wsUrl from the rpcUrl by swapping the scheme. Most providers
  // host their WebSocket at the same host/path under wss://, so this works
  // for Helius/Triton/QuickNode/etc. The listener silently no-ops for RPCs
  // that don't support logsSubscribe (e.g. public mainnet) — failures land
  // in the dispatcher's catch.
  useEffect(() => {
    if (!rpcUrl) {
      // Custom selected with nothing entered. Report the SDK's own state
      // rather than a blank — it still holds whatever was configured last,
      // and showing that is the point of this readout.
      setEffective(readEffectiveConfig());
      return;
    }
    let cancelled = false;
    (async () => {
      const wsUrl = rpcUrl.replace(/^http/i, 'ws');
      setSdkConfig({ rpcUrl, wsUrl, eventsListener: eventsEnabled });
      setLegacySdkConfig({ rpcUrl });
      // The vault SDK maintains its own config singleton — keep its
      // listener wsUrl + toggle in lockstep so the playground sees
      // slyvault events alongside SRSLY events.
      setVaultSdkConfig({ rpcUrl, wsUrl, eventsListener: eventsEnabled });
      setEffective(readEffectiveConfig());
      try {
        const genesis = await detectProgramSet(rpcUrl);
        if (cancelled) return;
        const network = networkForGenesis(genesis);
        if (network) {
          // The SDK's `programs` field selects the on-chain address set
          // (SAGE, ATLAS, ProfileFaction, etc.) — pass the network name
          // and the SDK looks up the matching addresses.
          setSdkConfig({ programs: network });
          setLegacySdkConfig({ programs: network });
        }
        // Re-read after `programs` lands: it feeds RPC resolution, so the
        // effective endpoint can change even though `rpcUrl` did not.
        setEffective(readEffectiveConfig());
      } catch {
        // Genesis lookup failed (network down, CORS, etc.) — leave the SDK
        // on whatever network it had; the user will see an RPC error from
        // their first instruction call instead.
        if (!cancelled) setEffective(readEffectiveConfig());
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [rpcUrl, eventsEnabled]);

  // When the user toggles events off (or switches RPCs), tear down both
  // live WS singletons (srsly + slyvault) so we stop receiving notifications.
  // Re-enabling pulls fresh subscriptions on the next `on*Event` call.
  useEffect(() => {
    if (!eventsEnabled) {
      void audit.closeSrslyEventListener();
      void vaultAudit.closeSlyvaultEventListener();
    }
  }, [eventsEnabled, rpcUrl]);

  return (
    <NetworkContext.Provider value={{ ...value, effective }}>{children}</NetworkContext.Provider>
  );
}

export function useNetwork(): NetworkContextValue {
  const ctx = useContext(NetworkContext);
  if (!ctx) {
    throw new Error('useNetwork must be used inside <NetworkProvider>');
  }
  return ctx;
}
